import { generateRootObjectSchema, generateStringSchema } from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";

// Invisible, server-only redirect card. The live-site behavior runs in the web app
// (YinzCam.Core.TeamMatchRedirect server hook): given a teamId (and no gameId), it resolves the
// team's current/default game and one-hops to that match in the correct section; with no current
// game it redirects to a configurable fallback slug. This spec exists so authors can place and
// configure the card in the CMS.
//
// Data binding: bind a `YinzCamAppServerGameBox` source for the team's current/default game and set
// the card transform to `match-redirect` (resolves gameId/homeTeamId/awayTeamId/compId). This is the
// same /Game/Box endpoint as Rule A, but pass teamId/compId params instead of id (both accept
// comma-separated lists), e.g. `/Game/Box?teamId={{teamId}}&compId={{compId}}`, templated from the
// page/query context; it returns the current/default game in the same Boxscore shape. notCompId is a
// link/section param consumed by section resolution, not a /Game/Box parameter.
export const specification = new BaseAtomSpec(
  "Team Match Redirect",
  generateRootObjectSchema({
    container: generateAtomContainerSchema(),
    gameId: generateStringSchema(
      "Game ID",
      "Resolved from the bound current/default game-box source via the `match-redirect` transform."
    ),
    homeTeamId: generateStringSchema(
      "Home Team ID",
      "Resolved from the bound current/default game-box source via the `match-redirect` transform."
    ),
    awayTeamId: generateStringSchema(
      "Away Team ID",
      "Resolved from the bound current/default game-box source via the `match-redirect` transform."
    ),
    compId: generateStringSchema(
      "Competition ID",
      "Resolved from the bound current/default game-box source via the `match-redirect` transform."
    ),
    teamIdParam: generateStringSchema(
      "Team ID Query Param",
      "Query parameter that gates the redirect (default: teamId).",
      { defaultValue: "teamId" }
    ),
    compIdParam: generateStringSchema(
      "Competition ID Query Param",
      "Query parameter for compId (default: compId).",
      { defaultValue: "compId" }
    ),
    gameIdParam: generateStringSchema(
      "Game ID Query Param",
      "Query parameter for gameId (default: gameId).",
      { defaultValue: "gameId" }
    ),
    targetSlug: generateStringSchema(
      "Target Slug",
      "Match-centre slug when a current game is resolved (default: current page slug, including slug1 when nested).",
      { defaultValue: "" }
    ),
    fallbackSlug: generateStringSchema(
      "Fallback Slug",
      "Page slug when no current game is available (e.g. fixtures).",
      { defaultValue: "" }
    ),
  })
);
